import styled from "@emotion/styled";
import { motion } from "framer-motion";

const PrivacyContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
`;

const PrivacyTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const PrivacyContent = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Section = styled.section`
  margin-bottom: ${(props) => props.theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.text};
  font-size: 1.8rem;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.textLight};
  line-height: 1.8;
  font-size: 1.1rem;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: ${(props) => props.theme.spacing.lg};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const ListItem = styled.li`
  color: ${(props) => props.theme.colors.textLight};
  line-height: 1.8;
  font-size: 1.1rem;
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const ContactInfo = styled.div`
  margin-top: ${(props) => props.theme.spacing.lg};
`;

export const TermsOfService: React.FC = () => {
  return (
    <PrivacyContainer>
      <PrivacyTitle>Terms of Service</PrivacyTitle>
      <PrivacyContent>
        <Text>
          Welcome to <strong>Telazer LLC</strong> ("Company", "we", "our",
          "us"). These Terms of Service ("Terms") govern your use of our website
          and services ("Services"). By accessing or using our Services, you
          agree to be bound by these Terms. If you do not agree to these Terms,
          please do not use our Services.
        </Text>

        <Section>
          <SectionTitle>Use of Our Services</SectionTitle>
          <Text>
            You agree to use our Services only for lawful purposes and in a way
            that does not infringe the rights of, restrict, or inhibit anyone
            else's use and enjoyment of the Services. Prohibited behavior
            includes harassing or causing distress or inconvenience to any other
            user, transmitting obscene or offensive content, or disrupting the
            normal flow of dialogue within our Services.
          </Text>
        </Section>

        <Section>
          <SectionTitle>User Accounts</SectionTitle>
          <Text>
            To access certain features of our Services, you may be required to
            create an account with a username and password. You are responsible
            for maintaining the confidentiality of your account information and
            for all activities that occur under your account. You agree to
            notify us immediately of any unauthorized use of your account or any
            other breach of security.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Intellectual Property</SectionTitle>
          <Text>
            All content included in the Services, such as text, graphics, logos,
            images, and software, is the property of Telazer LLC or its content
            suppliers and protected by international copyright laws. The
            compilation of all content in the Services is the exclusive property
            of Telazer LLC and protected by international copyright laws.
          </Text>
        </Section>

        <Section>
          <SectionTitle>User Content</SectionTitle>
          <Text>
            You may be able to submit content to our Services, such as comments,
            reviews, and other materials ("User Content"). By submitting User
            Content, you grant Telazer LLC a worldwide, non-exclusive,
            royalty-free, perpetual, irrevocable, and fully sublicensable right
            to use, reproduce, modify, adapt, publish, translate, create
            derivative works from, distribute, and display such User Content
            throughout the world in any media.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Prohibited Conduct</SectionTitle>
          <Text>You agree not to:</Text>
          <List>
            <ListItem>
              Use the Services for any illegal purpose or in violation of any
              local, state, national, or international law.
            </ListItem>
            <ListItem>
              Engage in any conduct that is harmful, fraudulent, deceptive,
              threatening, harassing, defamatory, obscene, or otherwise
              objectionable.
            </ListItem>
            <ListItem>
              Interfere with or disrupt the operation of the Services or the
              servers or networks used to make the Services available.
            </ListItem>
            <ListItem>
              Upload or transmit any form of virus, worm, Trojan horse, or other
              malicious code.
            </ListItem>
            <ListItem>
              Attempt to gain unauthorized access to the Services, other
              accounts, computer systems, or networks connected to the Services.
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Termination</SectionTitle>
          <Text>
            We may terminate or suspend your access to our Services immediately,
            without prior notice or liability, for any reason whatsoever,
            including without limitation if you breach these Terms. Upon
            termination, your right to use the Services will immediately cease.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Disclaimer of Warranties</SectionTitle>
          <Text>
            The Services are provided "as is" and "as available" without any
            warranties of any kind, either express or implied, including, but
            not limited to, implied warranties of merchantability, fitness for a
            particular purpose, non-infringement, or course of performance.
            Telazer LLC does not warrant that the Services will be
            uninterrupted, error-free, or free of viruses or other harmful
            components.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Limitation of Liability</SectionTitle>
          <Text>
            In no event shall Telazer LLC, its directors, employees, partners,
            agents, suppliers, or affiliates be liable for any indirect,
            incidental, special, consequential, or punitive damages, including
            without limitation, loss of profits, data, use, goodwill, or other
            intangible losses, resulting from your use or inability to use the
            Services.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Governing Law</SectionTitle>
          <Text>
            These Terms and your use of the Services are governed by and
            construed in accordance with the laws of the State of Delaware,
            without regard to its conflict of law principles. You agree to
            submit to the personal jurisdiction of the state and federal courts
            located in United States for the purpose of litigating all such
            claims or disputes.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Changes to These Terms</SectionTitle>
          <Text>
            We may revise these Terms from time to time in our sole discretion.
            If we make changes, we will post the amended Terms on the Services
            and update the "Last Updated" date above. Your continued use of the
            Services following the posting of changes means that you accept and
            agree to the changes.
          </Text>
        </Section>

        <Section>
          <SectionTitle>Contact Us</SectionTitle>
          <Text>
            If you have any questions about these Terms, please contact us at:
          </Text>
          <ContactInfo>
            <Text>
              <strong>Telazer LLC</strong>
              <br />
              4 Peddlers Row #1153
              <br />
              Newark, DE 19702 US
              <br />
              info@telazer.com
            </Text>
          </ContactInfo>
        </Section>
      </PrivacyContent>
    </PrivacyContainer>
  );
};
